/**
 * Replaces a secret the user typed in the chat. The chat is stored (browser,
 * turn log, agent state) and replayed to the model on every turn: a secret
 * left in it ends up copied into the flow. The configuration gate reads this
 * mask as a credential still to paste in the panel.
 */
export const TYPED_SECRET_MASK = '••••••'

const AUTH_SCHEME_VALUE = /\b(?:bearer|basic)\s+(\S+)/gi

// The languages the Khub UI is offered in, Chinese aside: with no spaces, a
// value glued to its word cannot be told apart. A bare "clé", "clave",
// "chiave" or "Schlüssel" is left out: it also names a column or a field key.
const CREDENTIAL_WORDS = [
    // English, also written as is in every other language
    String.raw`api[\s_-]?key`,
    'apikey',
    String.raw`access[\s_-]?token`,
    'token',
    'password',
    'passwd',
    'pwd',
    String.raw`client[\s_-]?secret`,
    'secret',
    'authorization',
    // French
    'jeton',
    'mot de passe',
    'mdp',
    String.raw`cl[ée]\s+(?:d['’])?api`,
    // Spanish
    'contrase[ñn]a',
    String.raw`clave\s+(?:de\s+(?:la\s+)?)?api`,
    // German
    'passwort',
    'kennwort',
    String.raw`api[\s-]?schl[üu]ssel`,
    'zugangsschl[üu]ssel',
    // Italian
    String.raw`chiave\s+(?:(?:di|dell['’])\s*)?api`,
    // Portuguese
    'senha',
    String.raw`chave\s+(?:d[ae]\s+)?api`,
]

const CREDENTIAL_WORD = new RegExp(
    String.raw`\b(?:${CREDENTIAL_WORDS.join('|')})\b`,
    'gi',
)

/** "mon jeton d'API, utilise-le : X" — the value comes within a few words */
const MAX_WORDS_AFTER_CREDENTIAL_WORD = 6

const MIN_SECRET_LENGTH = 8

const SENTENCE_END = /[.!?]$/

const surroundingPunctuation = /^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g

// Braces belong to JSON (a card reply) or to a {{data reference}}, "://" to
// a URL, a quote INSIDE the value to JSON: none of them is a value the user
// typed as a secret. Quotes around the value are fine (mon token "abc…").
const STRUCTURED_TEXT = /[{}]|:\/\//
const JSON_QUOTE = /"/

// An address names the account, it is not its secret
const EMAIL = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/

/**
 * Letters with a digit or a lower-to-upper switch inside: "expiré",
 * "Authentication" or a form number is not a secret, while a token, a
 * password or a base64 value (often digit-free) nearly always has one. Any
 * symbol is allowed: password policies require one. A value without a
 * credential word nearby looks exactly like a spreadsheet or form id, so it
 * is never masked on shape alone.
 */
const looksLikeSecret = (candidate: string): boolean =>
    candidate.length >= MIN_SECRET_LENGTH &&
    !EMAIL.test(candidate) &&
    /[A-Za-z]/.test(candidate) &&
    (/\d/.test(candidate) || /[a-z][A-Z]/.test(candidate))

const secretIn = (word: string): string | undefined => {
    if (STRUCTURED_TEXT.test(word)) return undefined
    const candidate = word.replaceAll(surroundingPunctuation, '')
    return !JSON_QUOTE.test(candidate) && looksLikeSecret(candidate)
        ? candidate
        : undefined
}

const wordsOf = (line: string): string[] => line.trim().split(/\s+/)

const findValueAfterCredentialWord = (rest: string): string | undefined => {
    const [sameLine, ...nextLines] = rest.split('\n')
    // "voici mon token :⏎<value>": nothing left on the word's line, so the
    // value is the first word of the next one — never further
    if (!/[A-Za-z0-9]/.test(sameLine)) {
        const nextLine = nextLines.find((line) => line.trim() !== '') ?? ''
        return secretIn(wordsOf(nextLine)[0] ?? '')
    }
    const words = wordsOf(sameLine).slice(0, MAX_WORDS_AFTER_CREDENTIAL_WORD)
    for (const word of words) {
        const secret = secretIn(word)
        if (secret) return secret
        if (SENTENCE_END.test(word)) return undefined
    }
    return undefined
}

const findTypedSecrets = (text: string): Set<string> => {
    const secrets = new Set<string>()
    for (const match of text.matchAll(AUTH_SCHEME_VALUE)) {
        const secret = secretIn(match[1])
        if (secret) secrets.add(secret)
    }
    for (const match of text.matchAll(CREDENTIAL_WORD)) {
        const value = findValueAfterCredentialWord(
            text.slice((match.index ?? 0) + match[0].length),
        )
        if (value) secrets.add(value)
    }
    return secrets
}

/**
 * Masks every occurrence of each secret found, wherever it appears: the user
 * often repeats the token without the word that announced it.
 */
export const maskTypedSecrets = (text: string): string =>
    [...findTypedSecrets(text)]
        // A secret containing another must be replaced first
        .sort((a, b) => b.length - a.length)
        .reduce(
            (masked, secret) => masked.replaceAll(secret, TYPED_SECRET_MASK),
            text,
        )

/** A chat history, as replayed to the model or attached to a review */
export const maskTypedSecretsInMessages = <T extends { content: string }>(
    messages: T[],
): T[] =>
    messages.map((message) => ({
        ...message,
        content: maskTypedSecrets(message.content),
    }))
